import { Bundle } from '@/services/api/contracts/catalog/bundles';
import { Product } from '@/services/api/contracts/catalog/types';
import { usePrintCurrencyStringForCurrentCustomer } from '@/utils/price';
import { FunctionComponent, useMemo } from 'react';

interface BundlePriceBreakdownProps {
  price: number;
  products: Product[];
  bundle: Bundle;
}

const BundlePriceBreakdown: FunctionComponent<BundlePriceBreakdownProps> = ({ price, products, bundle }) => {
  const totalContribution = useMemo(() => {
    const contributions = products.map(product => {
      const count = bundle.bundledItems.find(item => item.catalogItem === product.id)?.count ?? 1;

      return product.price?.margins
        .filter(margin => margin.party === 'Msp' || margin.party === 'Customer')
        .map(margin => margin.contribution * count);
    }) as number[][];

    const total = contributions.reduce((acc, innerArray) => {
      return acc + innerArray.reduce((sum, number) => sum + number, 0);
    }, 0);

    return total;
  }, [products, bundle]);

  const printCurrencyString = usePrintCurrencyStringForCurrentCustomer();
  const printedPrice = printCurrencyString(totalContribution);

  return (
    <div>
      <div className="flex items-center gap-1">
        <span className="text-primary-std text-sm font-semibold">{printedPrice}</span>
        <span className="text-xs opacity-50">•</span>
        <span className="text-secondary-std text-sm font-medium">MSP</span>
        <span className="text-xs opacity-50">•</span>
        <span className="text-secondary-std text-sm font-medium">
          {(totalContribution / price).toLocaleString('en-GB', {
            style: 'percent',
            minimumFractionDigits: 1,
            maximumFractionDigits: 2
          })}
        </span>
      </div>
    </div>
  );
};

export default BundlePriceBreakdown;
